import { FC } from 'react'

import styled from 'styled-components'

import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { SpotifyCampaignAiUsageEnum } from 'src/Types/campaigns'

import { AiUsageOption } from './AiUsageOption'

const OPTIONS: Array<{
  value: SpotifyCampaignAiUsageEnum
  title: string
  description: string
}> = [
  {
    value: SpotifyCampaignAiUsageEnum.NONE,
    title: 'No AI used',
    description: 'Created and performed entirely by humans.',
  },
  {
    value: SpotifyCampaignAiUsageEnum.ASSISTED,
    title: 'AI-assisted',
    description:
      'Human-made track with AI used for mixing, mastering, or idea generation.',
  },
  {
    value: SpotifyCampaignAiUsageEnum.GENERATED,
    title: 'AI-generated',
    description:
      'AI generated the primary vocals, lyrics, or main instruments.',
  },
]

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  padding: 24px 16px 96px 16px;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    margin: 0 auto;
    width: 600px;
    padding: 64px 0px 96px 0px;
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const SubTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  align-items: center;
`

const ButtonsContainer = styled.div`
  position: fixed;
  bottom: 0px;
  left: 0px;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  display: flex;
  width: 100%;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 24px;
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`

export interface AiUsageModalContentProps {
  value: SpotifyCampaignAiUsageEnum | null
  isLoading?: boolean
  onChange: (value: SpotifyCampaignAiUsageEnum) => void
  onConfirm: () => void
  onClose: () => void
}

export const AiUsageModalContent: FC<AiUsageModalContentProps> = ({
  value,
  isLoading,
  onChange,
  onConfirm,
  onClose,
}) => (
  <Container>
    <TextContainer>
      <Title>Did you use AI to create this track?</Title>
      <SubTitle>
        Curators set their own preferences for AI content. Selecting the correct
        option ensures your track reaches the right curators and prevents
        submission rejections.
      </SubTitle>
    </TextContainer>
    <OptionsList>
      {OPTIONS.map((option) => (
        <AiUsageOption
          key={option.value}
          checked={value === option.value}
          title={option.title}
          description={option.description}
          onClick={() => onChange(option.value)}
        />
      ))}
    </OptionsList>
    <ButtonsContainer>
      <PrimaryDottedButton onClick={onClose}>Go Back</PrimaryDottedButton>
      <StyledCTAButton
        onClick={onConfirm}
        disabled={!value}
        loading={isLoading}
      >
        Save info
      </StyledCTAButton>
    </ButtonsContainer>
  </Container>
)
